import { DialogScriptLine, ScriptLineType } from '../../common/src/Types'
import { buildAnonImageStack, CharacterId, CHARACTERS, CharacterSprite, findCharacter, findSprite, hashHue } from '../../common/src/Characters'
import { GeneratedCharacterVisual, VnPlayerLine } from './Types'
import { getSceneById } from '../../common/src/Scenes'
import { EpisodesRow } from '../../common/src/EpisodesRow'
import { toast } from './toast'

const buildGeneratedCharacterVisual = (actor: string, generatedCharacter: DialogScriptLine['data']['generatedCharacter'], emotion: string | undefined): GeneratedCharacterVisual | null => {
  if (!generatedCharacter) {
    return null
  }
  return { images: buildAnonImageStack(generatedCharacter, emotion || 'neutral'), hue: hashHue(actor.toLowerCase().trim()) }
}

export const buildVnPlayerLines = (episode: EpisodesRow): VnPlayerLine[] => {
  const episodeCharacterIds = episode.character_ids || CHARACTERS.map(ch => ch.id)

  let sprite1: any
  let character1Id: CharacterId | string | null = null
  let generated1: GeneratedCharacterVisual | null = null
  let sprite2: any
  let character2Id: CharacterId | string | null = null
  let generated2: GeneratedCharacterVisual | null = null
  let characterActive: number = 0

  const scene = getSceneById(episode.scene_id)
  let lastSceneId = scene.id

  const findCharacterIdAndSprite = (l: DialogScriptLine): { id: CharacterId | string, sprite: CharacterSprite | null, generated: GeneratedCharacterVisual | null } | null => {
    const ch = findCharacter(l.data.actor || '')
    if (ch && episodeCharacterIds.includes(ch.id)) {
      const sprite = l.data.actor ? findSprite(ch, l.data.emotion, scene) : null
      if (!sprite) {
        return null
      }
      return { id: ch.id, sprite, generated: null }
    }
    if (l.data.actor && l.data.generatedCharacter) {
      return { id: l.data.actor.toLowerCase().trim(), sprite: null, generated: buildGeneratedCharacterVisual(l.data.actor, l.data.generatedCharacter, l.data.emotion) }
    }
    return null
  }

  const tmpLines: VnPlayerLine[] = []
  for (const l of episode.script || []) {
    if (l.type === ScriptLineType.DIALOG) {
      const info = findCharacterIdAndSprite(l)
      if (info) {
        if (!character1Id || character1Id === info.id) {
          sprite1 = info.sprite
          generated1 = info.generated
          character1Id = info.id
          characterActive = 1
        } else if (!character2Id || character2Id === info.id) {
          sprite2 = info.sprite
          generated2 = info.generated
          character2Id = info.id
          characterActive = 2
        } else {
          // character has to replace an existing character:
          // we replace the one that didnt speak last
          if (characterActive === 1) {
            sprite2 = info.sprite
            generated2 = info.generated
            character2Id = info.id
            characterActive = 2
          } else {
            sprite1 = info.sprite
            generated1 = info.generated
            character1Id = info.id
            characterActive = 1
          }
        }
      } else {
        if (l.data.actor) {
          characterActive = 0
        }
      }
      const actor = l.data.actor || ''
      tmpLines.push({
        sceneId: lastSceneId,
        type: l.type,
        actor,
        text: l.data.text || '',
        voicefile: l.data.voicefile || '',
        characterActive: actor ? characterActive : 0,
        character1Id: actor && character1Id ? character1Id : null,
        character1Sprite: actor ? sprite1 : null,
        character1Generated: actor ? generated1 : null,
        character2Id: actor && character2Id ? character2Id : null,
        character2Sprite: actor ? sprite2 : null,
        character2Generated: actor ? generated2 : null,
      })
    } else if (l.type === ScriptLineType.NARRATION) {
      tmpLines.push({
        sceneId: lastSceneId,
        type: l.type,
        actor: '',
        text: l.data.text || '',
        voicefile: l.data.voicefile || '',
        characterActive: 0,
        character1Id: character1Id ? character1Id : null,
        character1Sprite: sprite1 ?? null,
        character1Generated: generated1 ?? null,
        character2Id: character2Id ? character2Id : null,
        character2Sprite: sprite2 ?? null,
        character2Generated: generated2 ?? null,
      })
    } else {
      const newSceneId = l.data.sceneId || lastSceneId
      if (newSceneId === lastSceneId) {
        continue
      }
      lastSceneId = newSceneId
      sprite1 = undefined
      character1Id = null
      generated1 = null
      sprite2 = undefined
      character2Id = null
      generated2 = null
      characterActive = 0
      tmpLines.push({
        sceneId: newSceneId,
        type: l.type,
        actor: '',
        text: '',
        voicefile: '',
        characterActive: 0,
        character1Id: null,
        character1Sprite: null,
        character1Generated: null,
        character2Id: null,
        character2Sprite: null,
        character2Generated: null,
      })
    }
  }
  // don't add a location change at the end
  while (tmpLines.length > 0 && tmpLines[tmpLines.length - 1].type === ScriptLineType.LOCATION) {
    tmpLines.pop()
  }
  return tmpLines
}

export const debug = (..._data: any[]) => {
  // pass
  // console.debug(...data)
}

export const handleCopyingToClipboard = async (copyText: string) => {
  try {
    await navigator.clipboard.writeText(copyText)
  } catch {
    toast('Error when copying!', 'error')
  }
}
